import type { DocMigration } from "../docMigration";

export function isParameterRef(v: any) {
  return v?.type === "PARAMETER_REF";
}

export const OPTION_SET_ID_PREFIX = "optionSet_";
export const OPTION_ID_PREFIX = "option_";

/**
 * This migration is a follow-up to https://github.com/higharc/product/pull/16290, which made some
 * corrections to how parametric values are created/updated for a given entity's parameterValues/globalParameterValues
 * tables.
 *
 * The goal of the migration is to restore "corrupted" parameter values to their correct, non-parameterized values.
 * If the value is already parameterized, (aka it's a PARAMETER_REF), we don't need to do anything.
 *
 * This is only relevant for ParameterType.OPTION parameters
 */

function processParameterValues(json: any, parameterValues: any): any {
  if (!parameterValues || !Object.keys(parameterValues).length) {
    return parameterValues;
  }

  for (const paramId in parameterValues) {
    const parameter = json.localParameters[paramId] || json.globalParameters[paramId];

    if (!parameter || parameter.type !== "OPTION") {
      continue;
    }

    const parameterValue = parameterValues[paramId];

    if (isParameterRef(parameterValue)) {
      continue;
    }

    if (parameterValue.type !== "OPTION" || typeof parameterValue.value !== "string") {
      continue;
    }

    if (parameterValue.value.startsWith(OPTION_SET_ID_PREFIX)) {
      const optionSet = json.optionSets[parameterValue.value];

      if (!optionSet) {
        continue;
      }

      parameterValues[paramId] = {
        type: "PRIMITIVE",
        value: optionSet.defaultValue,
      };

      continue;
    }

    const referencedParameter =
      json.localParameters[parameterValue.value] || json.globalParameters[parameterValue.value];

    if (referencedParameter?.defaultValue) {
      parameterValues[paramId] = {
        type: "PRIMITIVE",
        value: referencedParameter.defaultValue,
      };

      continue;
    }
  }

  return parameterValues;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_454: DocMigration = {
  maxSchemaVersion: "0.0.454",
  description: "Corrects corrupted parameter ref values for parameter value tables",
  apply(json: any) {
    for (const id in json.contentTemplateInstances ?? {}) {
      const instance = json.contentTemplateInstances[id];

      if (!instance.parameterValues || !Object.keys(instance.parameterValues).length) {
        continue;
      }

      instance.parameterValues = processParameterValues(json, instance.parameterValues);
    }

    for (const id in json.planSetTemplateConfigs ?? {}) {
      const config = json.planSetTemplateConfigs[id];

      const hasParameterValues = config.parameterValues && !!Object.keys(config.parameterValues).length;
      const hasGlobalParameterValues =
        config.globalParameterValues && !!Object.keys(config.globalParameterValues).length;

      if (!hasParameterValues && !hasGlobalParameterValues) {
        continue;
      }

      if (hasParameterValues) {
        config.parameterValues = processParameterValues(json, config.parameterValues);
      }

      if (hasGlobalParameterValues) {
        config.globalParameterValues = processParameterValues(json, config.globalParameterValues);
      }
    }

    for (const id in json.viewports ?? {}) {
      const viewport = json.viewports[id];

      if (viewport.viewType !== "DRAWING") {
        continue;
      }

      if (!viewport.parameterValues || !Object.keys(viewport.parameterValues).length) {
        continue;
      }

      viewport.parameterValues = processParameterValues(json, viewport.parameterValues);
    }

    return json;
  },
};

export default migration_0_0_454;
